import { useState } from "react";
import axios from "axios";
import {
    Mail,
    Phone,
    User,
    MessageSquare,
    FileText,
    ArrowRight,
    ShieldCheck,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const API_URL = "https://cheapdubaihotels.com/api";

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));

        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !form.name.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.message.trim()
        ) {
            setError("Please fill in all required fields.");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            if (!executeRecaptcha) {
                setError(
                    "Security verification is not ready. Please refresh the page and try again."
                );
                return;
            }

            const token = await executeRecaptcha("contact_form");

            const res = await axios.post(`${API_URL}/contact`, {
                ...form,
                recaptcha_token: token,
            });

            if (res.data.success) {
                setSuccess(
                    "Your message has been sent successfully. Our team will get back to you shortly."
                );

                setForm({
                    name: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                });
            } else {
                setError(res.data.message || "Failed to send your message.");
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const inputClass =
        "w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 pl-12 pr-4 text-sm font-medium text-[#17344B] placeholder:text-slate-400 outline-none transition duration-200 focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/10";

    const labelClass =
        "mb-2 block text-sm font-bold text-[#17344B]";

    const iconClass =
        "absolute left-4 top-1/2 -translate-y-1/2 text-[#0B6E8E]";

    return (
        <div className="w-full">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_55px_rgba(6,47,79,0.09)]">
                {/* Brand accent */}
                <div className="h-1.5 bg-gradient-to-r from-[#062F4F] via-[#0B6E8E] to-[#F7B928]" />

                {/* Decorative shapes */}
                <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#0B6E8E]/[0.045]" />
                <div className="pointer-events-none absolute -bottom-28 -left-24 h-64 w-64 rounded-full bg-[#F7B928]/[0.10]" />

                <div className="relative p-5 sm:p-8 lg:p-10">
                    {/* Heading */}
                    <div className="mb-8">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#0B6E8E]/15 bg-[#0B6E8E]/[0.06] px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-[#0B6E8E]">
                            <span className="h-2 w-2 rounded-full bg-[#F7B928]" />
                            Contact Our Team
                        </div>

                        <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#062F4F] sm:text-4xl">
                            Let’s Plan Your{" "}
                            <span className="text-[#0B6E8E]">Dubai Stay</span>
                        </h2>

                        <div className="mt-4 h-1 w-16 rounded-full bg-[#F7B928]" />

                        <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                            Need help finding the right hotel or have a question about your
                            booking? Send us your enquiry and our team will be happy to help.
                        </p>
                    </div>

                    {/* Success message */}
                    {success && (
                        <div
                            role="status"
                            className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4"
                        >
                            <CheckCircle2
                                size={21}
                                className="mt-0.5 shrink-0 text-emerald-600"
                            />
                            <div>
                                <p className="font-bold text-emerald-800">
                                    Message sent successfully
                                </p>
                                <p className="mt-1 text-sm leading-6 text-emerald-700">
                                    {success}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div
                            role="alert"
                            className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4"
                        >
                            <AlertCircle
                                size={21}
                                className="mt-0.5 shrink-0 text-rose-600"
                            />
                            <div>
                                <p className="font-bold text-rose-800">
                                    Unable to send your message
                                </p>
                                <p className="mt-1 text-sm leading-6 text-rose-700">
                                    {error}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Contact form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                            {/* Name */}
                            <div>
                                <label htmlFor="contact-name" className={labelClass}>
                                    Your Name <span className="text-orange-500">*</span>
                                </label>

                                <div className="relative">
                                    <User size={17} className={iconClass} />
                                    <input
                                        id="contact-name"
                                        type="text"
                                        name="name"
                                        autoComplete="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="Enter your full name"
                                        className={inputClass}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <label htmlFor="contact-email" className={labelClass}>
                                    Email Address <span className="text-orange-500">*</span>
                                </label>

                                <div className="relative">
                                    <Mail size={17} className={iconClass} />
                                    <input
                                        id="contact-email"
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        placeholder="you@example.com"
                                        className={inputClass}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                            {/* Phone */}
                            <div>
                                <label htmlFor="contact-phone" className={labelClass}>
                                    Phone Number <span className="text-orange-500">*</span>
                                </label>

                                <div className="relative">
                                    <Phone size={17} className={iconClass} />
                                    <input
                                        id="contact-phone"
                                        type="tel"
                                        name="phone"
                                        autoComplete="tel"
                                        value={form.phone}
                                        onChange={handleChange}
                                        placeholder="+971 50 123 4567"
                                        className={inputClass}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Subject */}
                            <div>
                                <label htmlFor="contact-subject" className={labelClass}>
                                    Subject <span className="text-slate-400">(Optional)</span>
                                </label>

                                <div className="relative">
                                    <FileText size={17} className={iconClass} />
                                    <input
                                        id="contact-subject"
                                        type="text"
                                        name="subject"
                                        value={form.subject}
                                        onChange={handleChange}
                                        placeholder="Hotel enquiry, booking help..."
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Message */}
                        <div>
                            <label htmlFor="contact-message" className={labelClass}>
                                Your Message <span className="text-orange-500">*</span>
                            </label>

                            <div className="relative">
                                <MessageSquare
                                    size={17}
                                    className="absolute left-4 top-4 text-[#0B6E8E]"
                                />

                                <textarea
                                    id="contact-message"
                                    name="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={handleChange}
                                    placeholder="Tell us what you need help with..."
                                    className={`${inputClass} resize-y py-3.5`}
                                    required
                                />
                            </div>
                        </div>

                        {/* Footer and submit */}
                        <div className="flex flex-col gap-5 border-t border-slate-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0B6E8E]/10">
                                    <ShieldCheck size={21} className="text-[#0B6E8E]" />
                                </div>

                                <div>
                                    <p className="text-sm font-bold text-[#062F4F]">
                                        Your details are secure
                                    </p>
                                    <p className="mt-1 text-xs leading-5 text-slate-500">
                                        Our team will respond as soon as possible.
                                    </p>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="group inline-flex min-h-[52px] w-full items-center justify-center gap-3 rounded-xl bg-[#FF5A1F] px-7 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-orange-500/20 transition duration-300 hover:-translate-y-0.5 hover:bg-[#E94A14] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {loading ? (
                                    <>
                                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        Send Message
                                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
                                            <ArrowRight
                                                size={17}
                                                className="transition-transform duration-300 group-hover:translate-x-1"
                                            />
                                        </span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}